import React, { useEffect, useMemo, useRef, useState } from "react";

// Modules
import Chart from "chart.js/auto";

// App
import { capacityFor, isBlankValue, toNumber } from "../hotelData";
import { ApiPath, Notice, Section } from "./apiField";

// UI components
import { MenuItem, Select, Tooltip } from "@mui/material";

const ALL_ROOMS = "__all__";

export default function CapacitySection({ meeting, raw, brandColor }) {
  const { rooms, layouts, listPath, metric } = meeting;
  const [scope, setScope] = useState(ALL_ROOMS);
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const unlabelled = useMemo(
    () => rooms.filter((r) => r.shapesUnlabelled),
    [rooms],
  );

  const chartData = useMemo(() => {
    if (!layouts.length) return null;

    const scoped =
      scope === ALL_ROOMS
        ? rooms
        : rooms.filter((r) => String(r.index) === scope);

    const values = layouts.map((layout) => {
      const capacities = scoped
        .map((room) => capacityFor(room, layout.key))
        .filter((v) => v != null);
      return capacities.length ? Math.max(...capacities) : 0;
    });

    if (!values.some((v) => v > 0)) return null;

    return { labels: layouts.map((l) => l.label), values };
  }, [rooms, layouts, scope]);

  useEffect(() => {
    if (!canvasRef.current || !chartData) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return undefined;
    }

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: chartData.labels,
        datasets: [
          {
            label: "Capacity",
            data: chartData.values,
            backgroundColor: brandColor || "#c00d0d",
            borderRadius: 3,
            maxBarThickness: 34,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.parsed.x.toLocaleString()} people`,
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            title: { display: true, text: "People" },
            grid: { color: "rgba(0,0,0,0.06)" },
          },
          y: { grid: { display: false } },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartData, scope, brandColor]);

  const sizeUnit = metric || "m²";

  return (
    <Section
      id="capacity"
      title="Capacity by meeting room layout"
      subtitle={`${rooms.length} rooms · ${layouts.length} layouts`}
      path={listPath}
      raw={raw}
    >
      {!rooms.length ? (
        <Notice tone="warn">No per-room documents.</Notice>
      ) : (
        <>
          {unlabelled.length ? (
            <Notice tone="warn">
              {unlabelled.map((r) => r.name).join(", ")}: no{" "}
              <code>meetingRoomShapeList.values[].type</code>. Capacities are
              in the table, excluded from the chart.
            </Notice>
          ) : null}

          {chartData ? (
            <div className="rhp-chart-block">
              <div className="rhp-chart-block__head">
                <Select
                  size="small"
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                >
                  <MenuItem value={ALL_ROOMS}>All rooms (max)</MenuItem>
                  {rooms
                    .filter((r) => !r.shapesUnlabelled && r.shapes.length)
                    .map((r) => (
                      <MenuItem key={r.index} value={String(r.index)}>
                        {r.name}
                      </MenuItem>
                    ))}
                </Select>
                <ApiPath path={`${listPath}[].content.meetingRoomShapeList.values[]`} />
              </div>
              <div className="rhp-chart">
                <canvas ref={canvasRef} />
              </div>
            </div>
          ) : (
            <Notice tone="warn">No layout capacities.</Notice>
          )}

          <div className="rhp-table-wrap">
            <table className="rhp-table">
              <thead>
                <tr>
                  <th className="rhp-table__room">Meeting room</th>
                  <th className="rhp-table__num">
                    Size
                    <span className="rhp-table__unit">{sizeUnit}</span>
                  </th>
                  {layouts.map((l) => (
                    <th className="rhp-table__num" key={l.key}>
                      <Tooltip title={`${l.key} · ${l.label}`} arrow>
                        <span>{l.label}</span>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rooms.map((room) => (
                  <tr
                    key={room.index}
                    className={room.shapesUnlabelled ? "is-unlabelled" : ""}
                  >
                    <th scope="row">
                      <span className="rhp-table__name">{room.name}</span>
                      {room.features.length ? (
                        <span className="rhp-table__features">
                          {room.features.join(" · ")}
                        </span>
                      ) : null}
                    </th>
                    <td className="rhp-table__num">
                      {isBlankValue(room.size) ? "—" : room.size}
                    </td>
                    {room.shapesUnlabelled ? (
                      <td className="rhp-table__num" colSpan={layouts.length}>
                        <span className="rhp-table__unlabelled">
                          {room.shapes
                            .map((s) => (isBlankValue(s.value) ? "—" : s.value))
                            .join(" · ")}
                          <em>no layout types</em>
                        </span>
                      </td>
                    ) : (
                      layouts.map((l) => {
                        const value = capacityFor(room, l.key);
                        const rawValue = room.shapes.find(
                          (s) => s.key === l.key,
                        )?.value;
                        return (
                          <td className="rhp-table__num" key={l.key}>
                            {value != null
                              ? value.toLocaleString()
                              : isBlankValue(rawValue)
                                ? "—"
                                : rawValue}
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rhp-room-notes">
            {rooms
              .filter((r) => r.description || r.tour)
              .map((room) => (
                <div className="rhp-room-note" key={room.index}>
                  <h4>{room.name}</h4>
                  {room.description ? <p>{room.description}</p> : null}
                  {room.tour ? (
                    <a href={room.tour} target="_blank" rel="noopener noreferrer">
                      Open 3D tour
                    </a>
                  ) : null}
                  <ApiPath path={room.descriptionPath} />
                </div>
              ))}
          </div>
        </>
      )}
    </Section>
  );
}

export const hasAnyCapacity = (rooms) =>
  rooms.some((room) => room.shapes.some((s) => toNumber(s.value) != null));
